import {readSharedReport, readSharedView} from '../../navigation/useSharedReport';
import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';

const TableSearchContext = createContext(null);
export const useTableSearch = () => useContext(TableSearchContext);

export function TableSearchScope({ children, shareId }) {
  const [query, setQuery] = useState(() => { const value = readSharedReport(shareId).search; return typeof value === 'string' ? value : ''; });
  const tables = useRef(new Set());
  const selected = useRef(null);
  const links = useRef(new Map());
  const [hasToolbar, setHasToolbar] = useState(false);
  const registerLink = useCallback((id, link) => {
    links.current.set(id, link);
    return () => links.current.delete(id);
  }, []);
  const copyLink = useCallback(async () => {
    const id = selected.current || tables.current.values().next().value;
    const url = links.current.get(id)?.();
    if (!url) return 'Open a table to share it';
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
      await navigator.clipboard.writeText(url);
      return 'Link copied';
    } catch { return 'Link ready in address bar'; }
  }, []);
  const register = useCallback(id => {
    tables.current.add(id);
    if (readSharedView()?.id === id) selected.current = id;
    return () => { tables.current.delete(id); if (selected.current === id) selected.current = null; };
  }, []);
  const selectTable = useCallback(id => { selected.current = id; }, []);
  const isShareTarget = useCallback(id => (selected.current || tables.current.values().next().value) === id, []);
  return <TableSearchContext.Provider value={{query, setQuery, register, selectTable, isShareTarget, registerLink, copyLink, hasToolbar, setHasToolbar}}>{children}</TableSearchContext.Provider>;
}

export function SectionTableSearch() {
  const search = useTableSearch();
  const [status, setStatus] = useState('');
  const setHasToolbar = search?.setHasToolbar;
  useEffect(() => {
    setHasToolbar?.(true);
    return () => setHasToolbar?.(false);
  }, [setHasToolbar]);
  if (!search) return null;
  return <>
    <button type="button" className="rounded px-2 py-1 text-sm font-normal text-blue-700 hover:bg-blue-50" onClick={async event => { event.stopPropagation(); setStatus(await search.copyLink()); }}>Copy link</button>
    <span role="status" className="sr-only">{status}</span>
    <input type="search" aria-label="Search tables in this section" placeholder="Search this section…"
    value={search.query} onChange={event => search.setQuery(event.target.value)}
    className="w-44 max-w-full rounded border border-gray-300 bg-white px-2 py-1 text-sm font-normal text-gray-900"
    title="Filters all tables in this section. Totals and exports include all rows." /></>;
}
